import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useLines } from '../../hooks/useLines';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { money, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { DateInput, Field, Select, TextInput } from '../../components/ui/Form';
import { PeriodBar, financialYear } from '../../components/accounts/PeriodBar';
import { AccountSelect, type Account as PickerAccount } from '../../components/accounts/AccountSelect';
import { CellInput, LineRow, LineTable } from '../../components/ui/LineTable';
import { JournalDrawer } from './JournalDrawer';
import { useBranches } from '../../hooks/useBranches';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { DayBookDayCard, DayBookSummary, type DayBook } from './DayBookView';

/** This calendar month, which is how a day book is usually read. */
function thisMonth(): { from: string; to: string } {
  const d = new Date();
  const iso = (x: Date): string =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return {
    from: iso(new Date(d.getFullYear(), d.getMonth(), 1)),
    to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

interface Account {
  id: string;
  code: string;
  name: string;
  allow_manual_entry: boolean;
}

interface Line {
  accountId: string;
  debit: string;
  credit: string;
  narration: string;
}

const blankLine = (): Line => ({ accountId: '', debit: '', credit: '', narration: '' });

/**
 * The day book, and the one place an entry can be written by hand.
 *
 * Almost everything here was posted by a document — a sale, a purchase, a till
 * count. A manual entry is the exception, needs its own permission, and still
 * has to balance before it will post.
 */
export function JournalsPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const lines = useLines<Line>(blankLine);

  const [from, setFrom] = useState(thisMonth().from);
  const [to, setTo] = useState(thisMonth().to);
  const [branchId, setBranchId] = useState('');
  const [newestFirst, setNewestFirst] = useState(true);
  const { branches } = useBranches();
  const { header: letterhead } = usePrintHeader();
  const [open, setOpen] = useState<string | null>(null);
  const [entry, setEntry] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [header, setHeader] = useState({ date: today(), narration: '', type: 'JOURNAL' });

  const query = new URLSearchParams();
  const fy = financialYear();
  query.set('from', from || fy.from);
  query.set('to', to || fy.to);
  if (branchId) query.set('branchId', branchId);

  // Every entry of the period, day by day, with each day's opening and closing.
  const list = useQuery({
    queryKey: ['journals', 'day-book', query.toString()],
    queryFn: () => api.get<DayBook>(`/day-book?${query.toString()}`),
  });
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ data: Account[] }>('/accounts'),
    staleTime: 300_000,
  });

  const totalDebit = lines.values.reduce((a, l) => a + Number(l.debit || 0), 0);
  const totalCredit = lines.values.reduce((a, l) => a + Number(l.credit || 0), 0);
  const balanced = totalDebit > 0 && Math.abs(totalDebit - totalCredit) < 0.005;
  /** Positive when debits lead, so the difference belongs on the credit side. */
  const difference = Number((totalDebit - totalCredit).toFixed(2));

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/journals',
        {
          date: header.date,
          type: header.type,
          narration: header.narration,
          lines: lines.values.map((l) => ({
            accountId: l.accountId,
            ...(Number(l.debit) > 0 ? { debit: l.debit } : {}),
            ...(Number(l.credit) > 0 ? { credit: l.credit } : {}),
            ...(l.narration ? { narration: l.narration } : {}),
          })),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: () => {
      setEntry(false);
      lines.reset();
      setHeader({ ...header, narration: '' });
      setError(null);
      void client.invalidateQueries({ queryKey: ['journals'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const book = list.data;
  const days = book?.days ?? [];
  const ordered = newestFirst ? [...days].reverse() : days;
  const shownOf = (d: (typeof days)[number]) =>
    typeFilter ? d.entries.filter((e) => e.journalType === typeFilter) : d.entries;
  const branchName = branches.find((b) => b.id === branchId)?.name;

  return (
    <>
      <PageHeader
        title="Day book"
        subtitle="Every entry posted, in order. Almost all of them were raised by a document."
        actions={
          <>
            {days.length > 0 ? (
              <button className="btn-ghost" onClick={() => window.print()}>
                Print
              </button>
            ) : null}
            {can('accounts.journal_manual') ? (
              <button className="btn-primary whitespace-nowrap" onClick={() => setEntry(true)}>
                Manual entry
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={error ?? list.error} />

      <PeriodBar
        from={from}
        to={to}
        onChange={(r) => {
          setFrom(r.from);
          setTo(r.to);
        }}
      >
        {branches.length > 1 ? (
          <div>
            <p className="label">Branch</p>
            <select className="field w-44" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <p className="label">Kind of entry</p>
          <select className="field w-44" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Everything</option>
            {['SALE', 'PURCHASE', 'RECEIPT', 'PAYMENT', 'CONTRA', 'JOURNAL'].map((t) => (
              <option key={t} value={t}>
                {t.charAt(0) + t.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>
      </PeriodBar>

      {/* On paper: whose book, and for when. */}
      <div className="hidden print:mb-4 print:block print:border-b-2 print:border-gold-600 print:pb-2 print:text-center">
        <p className="text-lg font-bold">{letterhead.companyName}</p>
        <p className="text-sm font-semibold uppercase tracking-wide">Day book</p>
        <p className="num text-xs">
          {from} to {to}
          {branchName ? ` · ${branchName}` : ' · all branches'}
          {typeFilter ? ` · ${typeFilter.toLowerCase()} entries only` : ''}
        </p>
      </div>

      {book && days.length > 0 ? <DayBookSummary book={book} /> : null}

      {days.length > 0 ? (
        <div className="no-print mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex gap-1 rounded-lg border border-silver-100 bg-surface p-0.5">
            {[
              [true, 'Newest day first'],
              [false, 'Oldest day first'],
            ].map(([v, label]) => (
              <button
                key={String(v)}
                className={`rounded-md px-3 py-1 text-xs ${newestFirst === v ? 'bg-velvet text-white' : 'text-ink-soft hover:text-ink'}`}
                onClick={() => setNewestFirst(v as boolean)}
              >
                {label as string}
              </button>
            ))}
          </div>
          {typeFilter ? (
            <span className="text-2xs text-ink-soft">
              Showing {typeFilter.toLowerCase()} entries only. Opening and closing balances still count every entry.
            </span>
          ) : null}
        </div>
      ) : null}

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && days.length === 0 ? (
        <>
          {book ? (
            <div className="mb-3 rounded-lg border border-silver-100 bg-surface px-4 py-2 text-sm shadow-card">
              <span className="text-ink-soft">Nothing posted in this period. Cash </span>
              <span className="num font-medium">{money(book.opening.cash)}</span>
              <span className="text-ink-soft"> and bank </span>
              <span className="num font-medium">{money(book.opening.bank)}</span>
              <span className="text-ink-soft"> stood unchanged throughout.</span>
            </div>
          ) : null}
          <Empty
            message="Nothing posted in that period."
            hint="Widen the dates, or choose a different kind of entry. Most entries are raised by a document — a bill, a purchase, a receipt — rather than by hand."
          />
        </>
      ) : null}

      {ordered.map((d) => {
        // Tomorrow, in the book's own order: the day this one's closing is carried to.
        const at = days.findIndex((x) => x.date === d.date);
        const next = days[at + 1]?.date ?? null;
        return <DayBookDayCard key={d.date} day={d} nextDate={next} shown={shownOf(d)} onOpen={setOpen} />;
      })}

      {open ? <JournalDrawer journalId={open} onClose={() => setOpen(null)} /> : null}

      {entry ? (
        <div className="rounded-xl border border-silver-100 bg-surface p-5 shadow-card">
          <div>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">Manual entry</h2>
                <p className="mt-1 text-sm text-ink-soft">
                  For the things no document covers. Control accounts are not offered — those are maintained by their
                  subledgers, and posting to one directly would put the two out of step.
                </p>
              </div>
              <button className="text-sm text-ink-soft underline" onClick={() => setEntry(false)}>
                Close
              </button>
            </div>

            <ErrorNotice error={error} />

            <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-5">
              <div className="card grid gap-3 md:grid-cols-3">
                <Field label="Date" required>
                  <DateInput value={header.date} onChange={(v) => setHeader({ ...header, date: v })} />
                </Field>
                <Field label="Type" required>
                  <Select
                    value={header.type}
                    onChange={(v) => setHeader({ ...header, type: v })}
                    options={[
                      { value: 'JOURNAL', label: 'Journal' },
                      { value: 'CONTRA', label: 'Contra (cash to bank)' },
                    ]}
                  />
                </Field>
                <Field label="Narration" required>
                  <TextInput
                    value={header.narration}
                    onChange={(v) => setHeader({ ...header, narration: v })}
                    placeholder="What this entry is for"
                  />
                </Field>
              </div>

              <LineTable
                head={['Account', 'Narration', 'Debit', 'Credit']}
                addLabel="Add line"
                onAdd={lines.add}
                footer={
                  <tfoot className="bg-gold-50/60">
                    <tr>
                      <td className="td" colSpan={3} />
                      <td className="td num text-right font-semibold">{money(totalDebit.toFixed(2))}</td>
                      <td className="td num text-right font-semibold">{money(totalCredit.toFixed(2))}</td>
                      <td className="td" />
                    </tr>
                  </tfoot>
                }
              >
                {lines.lines.map((line, index) => (
                  <LineRow
                    key={line.key}
                    index={index}
                    removable={lines.lines.length > 2}
                    onRemove={() => lines.remove(line.key)}
                  >
                    <td className="td">
                      <AccountSelect
                        accounts={(accounts.data?.data ?? []) as unknown as PickerAccount[]}
                        value={line.accountId}
                        onChange={(v) => lines.update(line.key, { accountId: v })}
                        postableOnly
                        placeholder="Account"
                        width="w-64"
                      />
                    </td>
                    <td className="td">
                      <CellInput
                        width="w-48"
                        value={line.narration}
                        onChange={(v) => lines.update(line.key, { narration: v })}
                      />
                    </td>
                    <td className="td">
                      <CellInput
                        numeric
                        decimals={2}
                        value={line.debit}
                        onChange={(v) => lines.update(line.key, { debit: v, credit: '' })}
                      />
                    </td>
                    <td className="td">
                      <CellInput
                        numeric
                        decimals={2}
                        value={line.credit}
                        onChange={(v) => lines.update(line.key, { credit: v, debit: '' })}
                      />
                      {/* The last line is nearly always "the rest of it", and
                          working that out on paper is where mistakes come from. */}
                      {difference !== 0 && !line.debit && !line.credit ? (
                        <button
                          type="button"
                          className="mt-1 block text-2xs text-gold-700 underline"
                          onClick={() =>
                            lines.update(
                              line.key,
                              difference > 0
                                ? { credit: difference.toFixed(2), debit: '' }
                                : { debit: (-difference).toFixed(2), credit: '' },
                            )
                          }
                        >
                          Put {money(Math.abs(difference).toFixed(2))} here
                        </button>
                      ) : null}
                    </td>
                  </LineRow>
                ))}
              </LineTable>

              <div className="flex items-center gap-4">
                <button
                  className="btn-primary"
                  disabled={!balanced || header.narration.trim() === '' || create.isPending}
                >
                  {create.isPending ? 'Posting…' : 'Post entry'}
                </button>
                {totalDebit > 0 || totalCredit > 0 ? (
                  <span className={`text-sm ${balanced ? 'text-success' : 'text-danger'}`}>
                    {balanced
                      ? 'Debits equal credits.'
                      : `Out by ${money(Math.abs(totalDebit - totalCredit).toFixed(2))} — an entry that does not balance will not post.`}
                  </span>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
